import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { Globe, Book, Plus, Trash2, Edit, Search, Check, X, Languages, Download, Upload } from 'lucide-react';
import * as api from '../../../services/api';

// Common languages supported by Google Translate (subset of 189+)
const GOOGLE_SUPPORTED_LANGUAGES = [
  { code: 'sv', name: 'Svenska', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', flag: '🇧🇬' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱' },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino', flag: '🇵🇭' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪' },
  { code: 'la', name: 'Latin', nativeName: 'Latina', flag: '🇻🇦' },
];

const LanguageSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [glossary, setGlossary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('languages');
  
  // Language dialog state
  const [showAddLanguageDialog, setShowAddLanguageDialog] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  
  // Glossary dialog state
  const [showGlossaryDialog, setShowGlossaryDialog] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [termForm, setTermForm] = useState({ swedish: '', translations: {} });
  
  // Selected language for glossary editing
  const [selectedGlossaryLang, setSelectedGlossaryLang] = useState('en');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsData, glossaryData, adminNotifData] = await Promise.all([
        api.getLanguageSettings(),
        api.getGlossary(),
        api.getAdminNotificationEmail().catch(() => ({ admin_notification_email: '' }))
      ]);
      setSettings({
        ...settingsData,
        admin_notification_email: adminNotifData.admin_notification_email || '',
        auto_translate_content: settingsData.auto_translate_content ?? true,
        use_chatgpt_for_content: settingsData.use_chatgpt_for_content ?? true,
      });
      setGlossary(glossaryData);
    } catch (error) {
      toast.error('Kunde inte ladda data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLanguage = (langCode) => {
    if (!settings) return;
    
    const isEnabled = settings.enabled_languages.includes(langCode);
    
    if (isEnabled) {
      // Removing language
      if (settings.enabled_languages.length === 1) {
        toast.error('Minst ett språk måste vara aktiverat');
        return;
      }
      if (settings.default_language === langCode) {
        toast.error('Kan inte inaktivera standardspråket. Välj ett annat standardspråk först.');
        return;
      }
      setSettings({
        ...settings,
        enabled_languages: settings.enabled_languages.filter(l => l !== langCode),
      });
    } else {
      // Adding language
      setSettings({
        ...settings,
        enabled_languages: [...settings.enabled_languages, langCode],
      });
    }
  };

  const handleAddLanguage = (langCode) => {
    if (!settings.enabled_languages.includes(langCode)) {
      setSettings({
        ...settings,
        enabled_languages: [...settings.enabled_languages, langCode],
      });
      toast.success(`${getLanguageInfo(langCode)?.name || langCode} tillagd!`);
    }
    setShowAddLanguageDialog(false);
    setLanguageSearch('');
  };

  const handleRemoveLanguage = (langCode) => {
    if (settings.default_language === langCode) {
      toast.error('Kan inte ta bort standardspråket');
      return;
    }
    if (settings.enabled_languages.length === 1) {
      toast.error('Minst ett språk måste vara aktiverat');
      return;
    }
    setSettings({
      ...settings,
      enabled_languages: settings.enabled_languages.filter(l => l !== langCode),
    });
    toast.success('Språk borttaget');
  };

  const handleToggleTranslation = () => {
    if (!settings) return;
    setSettings({
      ...settings,
      translation_enabled: !settings.translation_enabled,
    });
  };

  const handleSetDefaultLanguage = (langCode) => {
    if (!settings) return;
    setSettings({
      ...settings,
      default_language: langCode,
    });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.updateLanguageSettings({
        translation_enabled: settings.translation_enabled,
        enabled_languages: settings.enabled_languages,
        default_language: settings.default_language,
        google_translate_api_key: settings.google_translate_api_key,
        auto_translate_content: settings.auto_translate_content,
        use_chatgpt_for_content: settings.use_chatgpt_for_content,
      });
      
      // Save admin notification email if changed
      if (settings.admin_notification_email !== undefined) {
        await api.updateAdminNotificationEmail(settings.admin_notification_email);
      }
      
      toast.success('Inställningar sparade!');
    } catch (error) {
      toast.error('Kunde inte spara inställningar');
    } finally {
      setSaving(false);
    }
  };

  // Glossary functions
  const getGlossaryTerms = () => {
    if (!glossary || !glossary.sv) return [];
    const targetLang = selectedGlossaryLang;
    const terms = glossary.sv[targetLang] || {};
    return Object.entries(terms).map(([swedish, translation]) => ({
      swedish,
      translation,
    }));
  };

  const handleAddTerm = () => {
    setEditingTerm(null);
    // Initialize translations for all enabled languages except Swedish
    const translations = {};
    settings.enabled_languages.filter(l => l !== 'sv').forEach(lang => {
      translations[lang] = '';
    });
    setTermForm({ swedish: '', translations });
    setShowGlossaryDialog(true);
  };

  const handleEditTerm = (term) => {
    setEditingTerm(term.swedish);
    // Get translations for all enabled languages
    const translations = {};
    settings.enabled_languages.filter(l => l !== 'sv').forEach(lang => {
      translations[lang] = glossary?.sv?.[lang]?.[term.swedish] || '';
    });
    setTermForm({ swedish: term.swedish, translations });
    setShowGlossaryDialog(true);
  };

  const handleDeleteTerm = async (swedishTerm) => {
    if (!window.confirm(`Är du säker på att du vill ta bort "${swedishTerm}"?`)) return;
    
    try {
      const newGlossary = { ...glossary };
      // Remove from all target languages
      Object.keys(newGlossary.sv || {}).forEach(lang => {
        if (newGlossary.sv[lang] && newGlossary.sv[lang][swedishTerm]) {
          delete newGlossary.sv[lang][swedishTerm];
        }
      });
      
      await api.updateGlossary(newGlossary);
      setGlossary(newGlossary);
      toast.success('Term borttagen!');
    } catch (error) {
      toast.error('Kunde inte ta bort term');
    }
  };

  const handleSaveTerm = async () => {
    if (!termForm.swedish.trim()) {
      toast.error('Svenskt ord krävs');
      return;
    }

    try {
      const newGlossary = { ...glossary };
      if (!newGlossary.sv) newGlossary.sv = {};
      
      // If editing and Swedish term changed, remove old entries
      if (editingTerm && editingTerm !== termForm.swedish) {
        Object.keys(newGlossary.sv).forEach(lang => {
          if (newGlossary.sv[lang] && newGlossary.sv[lang][editingTerm]) {
            delete newGlossary.sv[lang][editingTerm];
          }
        });
      }
      
      // Add/update translations for each language
      Object.entries(termForm.translations).forEach(([lang, translation]) => {
        if (!newGlossary.sv[lang]) newGlossary.sv[lang] = {};
        if (translation.trim()) {
          newGlossary.sv[lang][termForm.swedish] = translation.trim();
        }
      });
      
      await api.updateGlossary(newGlossary);
      setGlossary(newGlossary);
      setShowGlossaryDialog(false);
      toast.success(editingTerm ? 'Term uppdaterad!' : 'Term tillagd!');
    } catch (error) {
      toast.error('Kunde inte spara term');
    }
  };

  const handleExportGlossary = () => {
    const dataStr = JSON.stringify(glossary, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'catholic_glossary.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Ordlista exporterad!');
  };

  const handleImportGlossary = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        await api.updateGlossary(imported);
        setGlossary(imported);
        toast.success('Ordlista importerad!');
      } catch (error) {
        toast.error('Ogiltig JSON-fil');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const getLanguageInfo = (code) => {
    return GOOGLE_SUPPORTED_LANGUAGES.find(l => l.code === code) || { 
      code, 
      name: code.toUpperCase(), 
      nativeName: code,
      flag: '🌐' 
    };
  };

  const filteredLanguagesToAdd = GOOGLE_SUPPORTED_LANGUAGES.filter(lang => {
    const matchesSearch = lang.name.toLowerCase().includes(languageSearch.toLowerCase()) ||
                          lang.nativeName.toLowerCase().includes(languageSearch.toLowerCase()) ||
                          lang.code.toLowerCase().includes(languageSearch.toLowerCase());
    const notEnabled = !settings?.enabled_languages?.includes(lang.code);
    return matchesSearch && notEnabled;
  });

  if (loading) {
    return <div className="flex items-center justify-center p-8">Laddar...</div>;
  }

  const enabledLangsInfo = settings?.enabled_languages?.map(getLanguageInfo) || [];
  const glossaryTerms = getGlossaryTerms();

  return (
    <div className="space-y-6" data-testid="language-settings-manager">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Språk & Ordlista</h1>
        <p className="text-gray-600">Hantera språk, översättningar och katolsk terminologi</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="languages" data-testid="tab-languages">
            <Globe className="w-4 h-4 mr-2" />
            Språkinställningar
          </TabsTrigger>
          <TabsTrigger value="glossary" data-testid="tab-glossary">
            <Book className="w-4 h-4 mr-2" />
            Katolsk Ordlista
          </TabsTrigger>
        </TabsList>

        {/* Languages Tab */}
        <TabsContent value="languages" className="space-y-6">
          {/* Translation Toggle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Languages className="w-5 h-5 mr-2" />
                Översättning
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-gray-900">Aktivera översättningar</h3>
                  <p className="text-sm text-gray-600">
                    När inaktiverat visas endast standardspråket
                  </p>
                </div>
                <button
                  onClick={handleToggleTranslation}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.translation_enabled ? 'bg-yellow-500' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-translation"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings?.translation_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Enabled Languages */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Aktiverade Språk ({enabledLangsInfo.length})</CardTitle>
                <p className="text-sm text-gray-600 mt-1">Klicka på ett språk för att aktivera/inaktivera det</p>
              </div>
              <Button 
                onClick={() => setShowAddLanguageDialog(true)}
                size="sm"
                data-testid="add-language-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Fler språk
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {enabledLangsInfo.map((lang) => {
                  const isDefault = settings?.default_language === lang.code;
                  const isEnabled = settings?.enabled_languages?.includes(lang.code);
                  
                  return (
                    <div
                      key={lang.code}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                        isDefault
                          ? 'border-yellow-500 bg-yellow-50'
                          : isEnabled
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-200 bg-gray-50'
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{lang.flag}</span>
                        <div>
                          <h3 className="font-semibold text-gray-900">{lang.name}</h3>
                          <p className="text-xs text-gray-600">
                            {isDefault && <span className="text-yellow-600 font-medium">Standardspråk • </span>}
                            {lang.nativeName} ({lang.code.toUpperCase()})
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {/* Enable/Disable Toggle */}
                        {!isDefault && (
                          <button
                            onClick={() => handleToggleLanguage(lang.code)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              isEnabled ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            data-testid={`toggle-${lang.code}`}
                            title={isEnabled ? 'Inaktivera' : 'Aktivera'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        )}
                        {!isDefault && isEnabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefaultLanguage(lang.code)}
                            data-testid={`set-default-${lang.code}`}
                          >
                            Gör till standard
                          </Button>
                        )}
                        {isDefault && (
                          <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                            Standard
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Tips:</strong> Google Translate stöder 189+ språk. Du kan lägga till fler språk 
                  efter behov. Standardspråket används när översättningar saknas.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Google API Key */}
          <Card>
            <CardHeader>
              <CardTitle>Google Translate API</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API-nyckel
                </label>
                <Input
                  type="password"
                  placeholder="Ange din Google Translate API-nyckel"
                  value={settings?.google_translate_api_key || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    google_translate_api_key: e.target.value
                  })}
                  data-testid="google-api-key"
                />
                <p className="text-xs text-gray-500 mt-2">
                  <a 
                    href="https://cloud.google.com/translate/docs/setup" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Läs hur du får en API-nyckel →
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Auto-Translation Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Automatisk Översättning</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-gray-900">Auto-översätt nytt innehåll</h3>
                  <p className="text-sm text-gray-600">
                    Automatiskt översätt artiklar och meddelanden när de skapas
                  </p>
                </div>
                <button
                  onClick={() => setSettings({...settings, auto_translate_content: !settings?.auto_translate_content})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.auto_translate_content ? 'bg-yellow-500' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-auto-translate"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings?.auto_translate_content ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-gray-900">Använd ChatGPT för innehåll</h3>
                  <p className="text-sm text-gray-600">
                    Bättre kvalitet för religiöst innehåll (artiklar, meddelanden)
                  </p>
                </div>
                <button
                  onClick={() => setSettings({...settings, use_chatgpt_for_content: !settings?.use_chatgpt_for_content})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.use_chatgpt_for_content ? 'bg-yellow-500' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-chatgpt"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings?.use_chatgpt_for_content ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <p className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                <strong>Hybrid-system:</strong> ChatGPT används för artiklar och meddelanden (bättre katolsk terminologi), 
                medan Google Translate används för UI-element (snabbare och billigare).
              </p>
            </CardContent>
          </Card>

          {/* Admin Notification Email */}
          <Card>
            <CardHeader>
              <CardTitle>Admin-notifikationer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-post för systemnotifikationer
                </label>
                <Input
                  type="email"
                  placeholder="admin@fatimakrona.se"
                  value={settings?.admin_notification_email || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    admin_notification_email: e.target.value
                  })}
                  data-testid="admin-notification-email"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Få e-post när automatiska uppgifter misslyckas (bibelläsningar, kalendersynk, etc.)
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
              data-testid="save-settings-btn"
            >
              {saving ? 'Sparar...' : 'Spara Inställningar'}
            </Button>
          </div>
        </TabsContent>

        {/* Glossary Tab */}
        <TabsContent value="glossary" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Katolsk Terminologi-ordlista</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Ord som ska översättas korrekt med katolsk terminologi
                </p>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={handleExportGlossary} data-testid="export-glossary">
                  <Download className="w-4 h-4 mr-1" />
                  Exportera
                </Button>
                <label>
                  <Button variant="outline" size="sm" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-1" />
                      Importera
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportGlossary}
                    className="hidden"
                    data-testid="import-glossary"
                  />
                </label>
                <Button size="sm" onClick={handleAddTerm} data-testid="add-term-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Lägg till term
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Language selector for glossary view */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visa översättningar till:
                </label>
                <div className="flex flex-wrap gap-2">
                  {settings?.enabled_languages?.filter(l => l !== 'sv').map(langCode => {
                    const lang = getLanguageInfo(langCode);
                    return (
                      <Button
                        key={langCode}
                        variant={selectedGlossaryLang === langCode ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedGlossaryLang(langCode)}
                        className={selectedGlossaryLang === langCode ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-900' : ''}
                        data-testid={`glossary-lang-${langCode}`}
                      >
                        {lang.flag} {lang.name}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Terms table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        Svenska (källa)
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        {getLanguageInfo(selectedGlossaryLang)?.flag} {getLanguageInfo(selectedGlossaryLang)?.name}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900 w-24">
                        Åtgärder
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {glossaryTerms.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                          Inga termer för detta språk ännu. Lägg till din första term!
                        </td>
                      </tr>
                    ) : (
                      glossaryTerms.map((term, index) => (
                        <tr key={`term-${term.swedish || index}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {term.swedish}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {term.translation || <span className="text-gray-400 italic">Ej översatt</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditTerm(term)}
                                data-testid={`edit-term-${index}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTerm(term.swedish)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-term-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Om ordlistan:</strong> När Google Translate översätter text, kontrolleras först 
                  denna ordlista. Om ett svenskt ord finns här, används den angivna översättningen 
                  istället för Google's automatiska översättning. Det säkerställer korrekt katolsk terminologi.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Language Dialog */}
      <Dialog open={showAddLanguageDialog} onOpenChange={setShowAddLanguageDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lägg till språk</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Sök språk..."
                value={languageSearch}
                onChange={(e) => setLanguageSearch(e.target.value)}
                className="pl-10"
                data-testid="language-search"
              />
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredLanguagesToAdd.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleAddLanguage(lang.code)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  data-testid={`add-lang-${lang.code}`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{lang.flag}</span>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{lang.name}</p>
                      <p className="text-xs text-gray-500">{lang.nativeName} ({lang.code})</p>
                    </div>
                  </div>
                  <Plus className="w-4 h-4 text-gray-400" />
                </button>
              ))}
              {filteredLanguagesToAdd.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  {languageSearch ? 'Inga språk matchar sökningen' : 'Alla tillgängliga språk är redan aktiverade'}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Term Dialog */}
      <Dialog open={showGlossaryDialog} onOpenChange={setShowGlossaryDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTerm ? 'Redigera term' : 'Lägg till ny term'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Svenskt ord/fras *
              </label>
              <Input
                value={termForm.swedish}
                onChange={(e) => setTermForm({ ...termForm, swedish: e.target.value })}
                placeholder="t.ex. nattvarden"
                data-testid="term-swedish"
              />
            </div>
            
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Översättningar
              </label>
              <div className="space-y-3">
                {settings?.enabled_languages?.filter(l => l !== 'sv').map(langCode => {
                  const lang = getLanguageInfo(langCode);
                  return (
                    <div key={langCode} className="flex items-center space-x-3">
                      <span className="text-lg w-8">{lang.flag}</span>
                      <Input
                        value={termForm.translations[langCode] || ''}
                        onChange={(e) => setTermForm({
                          ...termForm,
                          translations: {
                            ...termForm.translations,
                            [langCode]: e.target.value
                          }
                        })}
                        placeholder={`${lang.name} översättning`}
                        data-testid={`term-${langCode}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowGlossaryDialog(false)}>
                Avbryt
              </Button>
              <Button 
                onClick={handleSaveTerm}
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900"
                data-testid="save-term-btn"
              >
                {editingTerm ? 'Uppdatera' : 'Lägg till'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LanguageSettingsManager;
